import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createNode,
  deleteNode,
  fetchMembraneMe,
  NodeHasFrozenArchiveError,
  type MembraneView,
  type NodeView,
} from '@/api/membrane';
import type { DeviceCaptureMode } from '@/api/deviceCapture';
import type { ArchiveNodeView } from '@/api/downgradeArchive';
import { isNodeLimitReachedView } from '@/lib/nodeListView';
import { resolveNodeCardStatus, resolveNodeVitality } from '@/lib/nodeCardStatus';
import { DEVICE_OFFLINE_RUN_HINT } from '@/lib/isDeviceLive';
import { useCabinetNodeRuntime } from '@/lib/useCabinetNodeRuntime';
import { useCabinetNodesJournalPreview, type NodeJournalPreviewState } from '@/lib/useCabinetNodesJournalPreview';
import { NodeDowngradeArchivePanel, useDowngradeArchive } from '@/components/nodes/NodeDowngradeArchivePanel';
import { NodeLastTrackPreview } from '@/components/nodes/NodeLastTrackPreview';
import { NodeOverflowHoldLine } from '@/components/nodes/NodeOverflowHoldLine';
import { NodeScenarioCell } from '@/components/nodes/NodeScenarioCell';

interface NodesPageProps {
  onOpenJournal: () => void;
  onOpenDeviceBoard: () => void;
  onOpenKeys?: (nodeId: string) => void;
}

export function NodesPage({ onOpenJournal, onOpenDeviceBoard, onOpenKeys }: NodesPageProps) {
  const [data, setData] = useState<MembraneView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // #2632 g8c: отказ удаления из-за замороженного архива — объяснение на карточке узла.
  const [deleteRefusal, setDeleteRefusal] = useState<NodeHasFrozenArchiveError | null>(null);

  const membraneId = data?.membrane.id ?? null;
  const runtime = useCabinetNodeRuntime(membraneId);
  const deviceIds = useMemo(
    () =>
      (data?.nodes ?? [])
        .map((node) => node.device?.mediaDeviceId ?? null)
        .filter((id): id is string => id != null),
    [data?.nodes],
  );
  const journalPreview = useCabinetNodesJournalPreview(membraneId, deviceIds);
  // #2619: архив понижения — один запрос на страницу, панель на узле.
  const archive = useDowngradeArchive();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchMembraneMe());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreateNode = async () => {
    setBusy(true);
    setError(null);
    try {
      await createNode();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось создать узел');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteNode = async (node: NodeView) => {
    const activeKeys = node.accessKeys.filter((key) => key.active).length;
    const message =
      activeKeys > 0
        ? `Удалить узел «${node.label}»? Будет отозвано активных ключей: ${activeKeys}. Сопряжение с устройством разорвётся.`
        : `Удалить узел «${node.label}»? Сопряжение и ключи узла будут удалены.`;
    if (!window.confirm(message)) return;

    setBusy(true);
    setError(null);
    setDeleteRefusal(null);
    try {
      await deleteNode(node.id);
      await load();
    } catch (e) {
      if (e instanceof NodeHasFrozenArchiveError) {
        setDeleteRefusal(e);
        // Ссылка ведёт на панель архива — она должна показывать то, о чём говорит отказ.
        void archive.reload();
      } else {
        setError(e instanceof Error ? e.message : 'Не удалось удалить узел');
      }
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <span className="loading loading-spinner loading-md" aria-label="Загрузка" />;
  }

  const nodes = data?.nodes ?? [];
  const limitReached = data ? isNodeLimitReachedView(nodes.length, data.membrane.tariff.maxNodesPerMembrane) : true;

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Узлы</h1>
        <p className="mt-2 text-base-content/70">
          Узел связывает мембрану с полевым клиентом. Запускайте мониторинг и переключайте режим
          тревоги по сети. Ключи доступа — в разделе «Ключи».
        </p>
        <p className="mt-1 text-xs text-base-content/50">
          Связь с узлами: {runtimeConnectionLabel(runtime.connection)}
        </p>
      </div>

      {error ? (
        <div className="alert alert-error">
          <span>{error}</span>
        </div>
      ) : null}

      {nodes.length === 0 ? (
        <div className="card bg-base-200">
          <div className="card-body">
            <p>Пока нет узлов.</p>
            <button
              type="button"
              className="btn btn-primary w-fit"
              disabled={busy}
              onClick={() => void handleCreateNode()}
            >
              Создать узел
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {nodes.map((node) => (
            <NodeCard
              key={node.id}
              node={node}
              runtime={runtime}
              journalPreview={journalPreview.getPreview(node.device?.mediaDeviceId ?? '')}
              archive={archive.nodes.find((a) => a.nodeId === node.id)}
              archiveLoadError={archive.error}
              onArchiveRestored={archive.reload}
              onArchiveRetry={archive.reload}
              deleteRefusal={deleteRefusal?.nodeId === node.id ? deleteRefusal : null}
              busy={busy}
              onOpenJournal={onOpenJournal}
              onOpenDeviceBoard={onOpenDeviceBoard}
              onOpenKeys={onOpenKeys}
              onDelete={() => void handleDeleteNode(node)}
            />
          ))}

          <button
            type="button"
            className="btn btn-outline w-fit"
            disabled={busy || limitReached}
            title={limitReached ? `Достигнут лимит тарифа (${data?.membrane.tariff.maxNodesPerMembrane})` : undefined}
            onClick={() => void handleCreateNode()}
          >
            Добавить узел
          </button>
        </div>
      )}
    </div>
  );
}

/** Якорь панели архива узла — цель ссылки из отказа удаления (#2632 g8c). */
function archiveAnchorId(nodeId: string): string {
  return `node-archive-${nodeId}`;
}

/**
 * Объяснение отказа удаления узла (#2632 g8c, Т3): почему нельзя и когда станет можно. Срок
 * уже прошёл, а архив ещё не убран — честно говорим «после уборки», а не дату из прошлого.
 */
function frozenArchiveRefusalText(nearestExpiresAt: string | null, now: number = Date.now()): string {
  const head = 'Узел не удалён: у прибора есть архив записей после понижения тарифа.';
  const at = nearestExpiresAt ? Date.parse(nearestExpiresAt) : Number.NaN;
  if (Number.isNaN(at)) return `${head} Удаление станет возможно, когда архив удалится по сроку.`;
  if (at <= now) return `${head} Срок архива истёк — удаление станет возможно после его очистки.`;
  const day = new Date(at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
  return `${head} Удаление станет возможно после ${day}, когда архив удалится по сроку.`;
}

function runtimeConnectionLabel(state: ReturnType<typeof useCabinetNodeRuntime>['connection']): string {
  switch (state) {
    case 'connected':
      return 'установлена';
    case 'connecting':
      return 'подключение…';
    case 'reconnecting':
      return 'переподключение…';
    default:
      return 'нет';
  }
}

function NodeCard({
  node,
  runtime,
  journalPreview,
  archive,
  archiveLoadError,
  onArchiveRestored,
  onArchiveRetry,
  deleteRefusal,
  busy,
  onOpenJournal,
  onOpenDeviceBoard,
  onOpenKeys,
  onDelete,
}: {
  node: NodeView;
  runtime: ReturnType<typeof useCabinetNodeRuntime>;
  journalPreview: NodeJournalPreviewState;
  archive: ArchiveNodeView | undefined;
  archiveLoadError: string | null;
  onArchiveRestored: () => Promise<void>;
  onArchiveRetry: () => Promise<void>;
  deleteRefusal: NodeHasFrozenArchiveError | null;
  busy: boolean;
  onOpenJournal: () => void;
  onOpenDeviceBoard: () => void;
  onOpenKeys?: (nodeId: string) => void;
  onDelete: () => void;
}) {
  const deviceId = node.device?.mediaDeviceId ?? null;
  const state = deviceId ? runtime.states[deviceId] : undefined;
  const capture = deviceId ? runtime.captures[deviceId] : undefined;
  // CX3: объявленный узлом список сценариев (dropdown под захватом).
  const scenarioList = deviceId ? runtime.scenarioLists[deviceId] : undefined;
  const deviceLive = runtime.isDeviceLive(deviceId);
  // #279: иерархия статуса карточки — ключ (revoked/expired) важнее транспорта.
  const cardStatus = resolveNodeCardStatus({
    paired: node.device != null,
    pairedKeyStatus: node.device?.pairedKeyStatus ?? null,
    deviceLive,
  });
  const isRunning = state?.isRunning ?? false;
  const mode = state?.mode ?? 'normal';
  /*
    M4 (б)/#2309, адаптер C-1: «жив · не пишет · буфер полон» — отдельное значение состояния узла,
    ортогональное фазе сценария. Один порог «умер» — presence-окно кабинета; время последнего
    heartbeat кабинет здесь не знает (null) и судит по presence.
  */
  const overflowHold = state?.overflowHold ?? null;
  const vitality = resolveNodeVitality({
    presenceOnline: deviceLive,
    lastPresenceAtMs: null,
    nowMs: Date.now(),
    overflowHold,
  });
  const [captureMode, setCaptureMode] = useState<DeviceCaptureMode>('soft');
  const [captureBusy, setCaptureBusy] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);
  const [deviceIdCopied, setDeviceIdCopied] = useState(false);

  useEffect(() => {
    if (!deviceIdCopied) return;
    const timer = window.setTimeout(() => setDeviceIdCopied(false), 2_000);
    return () => window.clearTimeout(timer);
  }, [deviceIdCopied]);

  const copyDeviceId = async (): Promise<void> => {
    if (!deviceId) return;
    try {
      await navigator.clipboard.writeText(deviceId);
      setDeviceIdCopied(true);
    } catch {
      /* клипборд недоступен (например http) — id остаётся выделяемым текстом */
    }
  };

  // CT3 (канон §1): без захвата у кабинета нет контроля над сценариями узла.
  const isCaptured = capture !== undefined;
  const canCapture = deviceId !== null && deviceLive && !captureBusy;

  const handleCapture = async () => {
    setCaptureBusy(true);
    setCaptureError(null);
    try {
      await runtime.captureDevice(node.id, captureMode);
    } catch (e) {
      setCaptureError(e instanceof Error ? e.message : 'Не удалось захватить устройство');
    } finally {
      setCaptureBusy(false);
    }
  };

  const handleRelease = async () => {
    setCaptureBusy(true);
    setCaptureError(null);
    try {
      await runtime.releaseDevice(node.id);
    } catch (e) {
      setCaptureError(e instanceof Error ? e.message : 'Не удалось отпустить устройство');
    } finally {
      setCaptureBusy(false);
    }
  };

  const statusBadge = !isRunning ? (
    <span className="badge badge-ghost">Остановлен</span>
  ) : (
    <span className={`badge ${mode === 'alarm' ? 'badge-warning' : 'badge-info'}`}>
      {mode === 'alarm' ? 'Тревога' : 'Работает'}
    </span>
  );

  // CT3 (канон §7): состояние захвата видно с обеих сторон.
  const captureBadge = isCaptured ? (
    <span
      className={`badge ${capture.mode === 'hard' ? 'badge-warning' : 'badge-info'}`}
      title={
        capture.mode === 'hard'
          ? 'Устройство полностью ведомое; поле только смотрит'
          : 'Поле может запускать и останавливать сценарии; правка и пауза заблокированы'
      }
    >
      {capture.mode === 'hard' ? 'Захвачено: жёсткий' : 'Захвачено: мягкий'}
    </span>
  ) : null;

  const borderClass = isRunning
    ? mode === 'alarm'
      ? 'border-warning'
      : 'border-info animate-pulse'
    : 'border-transparent';

  return (
    <div className={`card border-2 bg-base-200 ${borderClass}`}>
      <div className="card-body gap-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 className="card-title text-lg">{node.label}</h2>
            <p className="font-mono text-xs text-base-content/50">{node.id}</p>
            {deviceId ? (
              /* CX1: с каким устройством сопряжён узел — полный id, копируется;
                 зеркально NB1 «Сопряжённое устройство» на клиенте (сверка глазами). */
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <span className="text-xs text-base-content/60">связан с устройством</span>
                <span
                  className="select-all break-all font-mono text-xs font-medium text-primary"
                  data-testid="node-paired-device-id"
                >
                  {deviceId}
                </span>
                <button
                  type="button"
                  className="btn btn-ghost btn-xs"
                  onClick={() => void copyDeviceId()}
                  aria-label="Скопировать id устройства"
                >
                  {deviceIdCopied ? '✓ скопировано' : 'копировать'}
                </button>
              </div>
            ) : null}
            {/* #279: истёкший/отозванный ключ важнее online/offline — оператору
                нужно действовать (перевыпустить ключ), а не ждать связи. */}
            {cardStatus === 'not-paired' ? (
              <span className="badge badge-ghost badge-sm mt-1">не сопряжён</span>
            ) : cardStatus === 'key-revoked' ? (
              <span
                className="badge badge-error badge-sm mt-1"
                title="Ключ сопряжения отозван. Выпустите новый ключ и пересопрягите устройство"
              >
                ключ отозван
              </span>
            ) : cardStatus === 'key-expired' ? (
              <span
                className="badge badge-warning badge-sm mt-1"
                title={`Ключ истёк${
                  node.device?.pairedKeyExpiresAt
                    ? ` ${new Date(node.device.pairedKeyExpiresAt).toLocaleString()}`
                    : ''
                }. Выпустите новый ключ и пересопрягите устройство`}
              >
                ключ устарел
              </span>
            ) : cardStatus === 'online' ? (
              <span className="badge badge-success badge-sm mt-1">online</span>
            ) : (
              <span className="badge badge-warning badge-sm mt-1">сопряжён · offline</span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {captureBadge}
            {statusBadge}
          </div>
        </div>

        <span className="sr-only" role="status" aria-live="polite">
          {`${
            isCaptured
              ? `Устройство захвачено (${capture.mode === 'hard' ? 'жёсткий' : 'мягкий'} режим). `
              : ''
          }${
            isRunning
              ? `Режим: ${mode === 'alarm' ? 'тревога' : 'обычный'}`
              : vitality === 'stopped_buffer_full'
                ? 'Остановлен: буфер полон — узел жив, не пишет'
                : 'Сценарий остановлен'
          }`}
        </span>

        <NodeOverflowHoldLine vitality={vitality} hold={overflowHold} />

        {captureError ? (
          <div className="alert alert-error py-2 text-sm">
            <span>{captureError}</span>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          {!isCaptured ? (
            // CT3 шаг 1 (канон §1): явный захват — до него контроля нет.
            <>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                disabled={!canCapture}
                title={
                  !deviceId
                    ? 'Узел не сопряжён с устройством'
                    : !deviceLive
                      ? DEVICE_OFFLINE_RUN_HINT
                      : undefined
                }
                onClick={() => void handleCapture()}
              >
                Захватить
              </button>
              <label className="flex items-center gap-1.5 text-xs text-base-content/70">
                <span>Режим</span>
                <select
                  className="select select-bordered select-xs"
                  value={captureMode}
                  onChange={(event) => setCaptureMode(event.target.value as DeviceCaptureMode)}
                  aria-label="Режим захвата устройства"
                >
                  <option value="soft">мягкий</option>
                  <option value="hard">жёсткий</option>
                </select>
              </label>
            </>
          ) : (
            // CT3 шаг 2 + CX3: под захватом — запуск/остановка выбранного сценария.
            // Выбор сценария вынесен в отдельную ячейку под треком (NodeScenarioCell).
            <>
              {!isRunning ? (
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  disabled={!deviceId || !deviceLive}
                  title={
                    !deviceLive
                      ? DEVICE_OFFLINE_RUN_HINT
                      : scenarioList && scenarioList.scenarios.length > 0
                        ? 'Запустить выбранный сценарий'
                        : 'Запустить сохранённый сценарий устройства'
                  }
                  onClick={() => deviceId && runtime.run(deviceId)}
                >
                  Пуск
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-sm btn-error"
                  disabled={!deviceId}
                  title="Остановить с плавным затуханием (200 мс)"
                  onClick={() => deviceId && runtime.stop(deviceId)}
                >
                  Стоп
                </button>
              )}
              <button
                type="button"
                className="btn btn-sm btn-outline"
                disabled={captureBusy}
                title="Отпустить управление; играющий сценарий продолжит играть"
                onClick={() => void handleRelease()}
              >
                Отпустить
              </button>
            </>
          )}

          <div className="ml-auto flex gap-2">
            {onOpenKeys ? (
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                disabled={busy}
                onClick={() => onOpenKeys(node.id)}
              >
                Ключи
              </button>
            ) : null}
            <button type="button" className="btn btn-sm btn-ghost" onClick={onOpenJournal}>
              Журнал
            </button>
            <button type="button" className="btn btn-sm btn-ghost" onClick={onOpenDeviceBoard}>
              Доска
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline btn-error"
              disabled={busy}
              onClick={onDelete}
            >
              Удалить
            </button>
          </div>
        </div>

        {deviceId ? (
          <NodeLastTrackPreview
            deviceId={deviceId}
            deviceLive={deviceLive}
            lastTrack={journalPreview.lastTrack}
            loading={journalPreview.loading}
          />
        ) : null}

        {deleteRefusal ? (
          <div className="alert alert-warning py-2 text-sm" role="alert" data-testid="node-delete-refusal">
            <span>
              {frozenArchiveRefusalText(deleteRefusal.nearestExpiresAt)}{' '}
              <a className="link" href={`#${archiveAnchorId(node.id)}`}>
                Открыть архив прибора
              </a>
            </span>
          </div>
        ) : null}

        <div id={archiveAnchorId(node.id)}>
          <NodeDowngradeArchivePanel
            view={archive}
            loadError={archiveLoadError}
            onRetryLoad={onArchiveRetry}
            onRestored={onArchiveRestored}
          />
        </div>

        {isCaptured && deviceId ? (
          <NodeScenarioCell
            scenarios={scenarioList?.scenarios ?? []}
            selectedScenarioId={scenarioList?.selectedScenarioId ?? null}
            onSelect={(id) => runtime.selectScenario(deviceId, id)}
            disabled={!deviceLive}
          />
        ) : null}
      </div>
    </div>
  );
}
